// Controller Stimulus pour gérer le formulaire de création/continuation de régularisation
// Permet de basculer entre "Nouvelle régularisation" et "Continuer une régularisation existante"
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'newForm', // Formulaire pour créer une nouvelle régularisation
    'continueForm', // Formulaire pour continuer une régularisation existante
    'newRadio', // Radio button "Nouvelle"
    'continueRadio', // Radio button "Continuer"
    'labelInput', // Input du label (caché en mode "continuer")
    'labelDisplay', // Affichage du label (en mode "continuer")
    'select', // Select pour choisir la régularisation à continuer
    'cumulInfo', // Affichage du cumul actuel
    'referenceIdInput', // Input caché pour le reference_id
  ];

  connect() {
    // Initialiser l'affichage selon l'état par défaut (nouveau)
    this.toggleMode();

    // Si on a un select, initialiser son affichage
    if (this.hasSelectTarget) {
      this.updateLabelFromSelect();
    }
  }

  // Appelé quand on change de radio button
  toggleMode() {
    const isNewMode = this.hasNewRadioTarget && this.newRadioTarget.checked;

    if (isNewMode) {
      // Mode "Nouvelle régularisation"
      this.showNewForm();
    } else {
      // Mode "Continuer une régularisation"
      this.showContinueForm();
    }
  }

  showNewForm() {
    // Afficher le formulaire de création
    if (this.hasNewFormTarget) {
      this.newFormTarget.classList.remove('hidden');
      // Activer tous les inputs du formulaire "nouveau"
      this.enableFormInputs(this.newFormTarget);
    }

    // Masquer le formulaire de continuation
    if (this.hasContinueFormTarget) {
      this.continueFormTarget.classList.add('hidden');
      // Désactiver tous les inputs du formulaire "continuer" pour qu'ils ne soient pas soumis
      this.disableFormInputs(this.continueFormTarget);
    }

    // Réinitialiser le label input pour permettre la saisie libre
    if (this.hasLabelInputTarget) {
      this.labelInputTarget.disabled = true; // Désactivé car dans le formulaire "continuer"
      this.labelInputTarget.value = '';
    }
  }

  showContinueForm() {
    // Masquer le formulaire de création
    if (this.hasNewFormTarget) {
      this.newFormTarget.classList.add('hidden');
      // Désactiver tous les inputs du formulaire "nouveau" pour qu'ils ne soient pas soumis
      this.disableFormInputs(this.newFormTarget);
    }

    // Afficher le formulaire de continuation
    if (this.hasContinueFormTarget) {
      this.continueFormTarget.classList.remove('hidden');
      // Activer tous les inputs du formulaire "continuer"
      this.enableFormInputs(this.continueFormTarget);
    }

    // Pré-remplir le label avec la régularisation sélectionnée
    if (this.hasSelectTarget) {
      this.updateLabelFromSelect();
    }
  }

  // Appelé quand on change la sélection dans le dropdown
  updateLabelFromSelect() {
    if (!this.hasSelectTarget) return;

    const selectedOption = this.selectTarget.options[this.selectTarget.selectedIndex];
    if (!selectedOption) return;

    // Récupérer les données de l'option sélectionnée
    const label = selectedOption.dataset.label;
    const cumul = selectedOption.dataset.cumul;
    const referenceId = selectedOption.value;

    // Afficher le label en lecture seule
    if (this.hasLabelDisplayTarget) {
      this.labelDisplayTarget.textContent = label;
    }

    // Afficher le cumul actuel
    if (this.hasCumulInfoTarget && cumul) {
      const formattedCumul = this.formatCurrency(parseFloat(cumul));
      this.cumulInfoTarget.innerHTML = `
        <div class="text-sm text-neutral-600 mt-2 p-3 bg-info-50 border border-info-200 rounded-lg">
          <strong>Cumul actuel :</strong> ${formattedCumul}
        </div>
      `;
    }

    // Mettre à jour les champs cachés
    if (this.hasLabelInputTarget) {
      this.labelInputTarget.value = label;
    }

    if (this.hasReferenceIdInputTarget) {
      this.referenceIdInputTarget.value = referenceId;
    }
  }

  // Formate un nombre en devise française
  formatCurrency(value) {
    return (
      new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value) + ' €'
    );
  }

  // Désactive tous les inputs/select/textarea d'un formulaire
  // pour qu'ils ne soient pas soumis avec le formulaire
  disableFormInputs(formElement) {
    if (!formElement) return;

    const inputs = formElement.querySelectorAll('input, select, textarea');
    inputs.forEach((input) => {
      input.disabled = true;
    });
  }

  // Active tous les inputs/select/textarea d'un formulaire
  enableFormInputs(formElement) {
    if (!formElement) return;

    const inputs = formElement.querySelectorAll('input, select, textarea');
    inputs.forEach((input) => {
      input.disabled = false;
    });
  }
}
